import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import type { ReportColumn } from '../models/column.types'
import type { ReportRow } from '../models/report.types'
import type { ReportTableSortV1 } from '../models/tableConfig.types'
import {
  buildComparisonRows,
  buildExpandedComparisonColumns,
  getExpandableComparisonFields,
} from '../service/comparisonColumns'
import { normalizeReportTableRows } from '../service/reportMediaPresentation'
import { sortRows } from '../service/sortRows'

import { useI18n } from '@/shared/i18n'
import { markTransformEnd, markTransformStart } from '@/shared/lib/perf/perfMarks'

export interface UseReportTableRenderOptions {
  reportId: MaybeRefOrGetter<number>
  rows: MaybeRefOrGetter<readonly ReportRow[]>
  columns: MaybeRefOrGetter<readonly ReportColumn[]>
  expandedFields: MaybeRefOrGetter<readonly string[]>
  sort: MaybeRefOrGetter<ReportTableSortV1 | null>
  enabled?: MaybeRefOrGetter<boolean>
  getColumn: (field: string) => ReportColumn | null | undefined
}

/** Shapes report-specific and comparison-only data without contaminating request/view state. */
export function useReportTableRender(options: UseReportTableRenderOptions) {
  const { t } = useI18n()
  const baseRows = computed(() => {
    // Perf transform span (ORP-22839) — no-op unless the switch is on. This computed IS the
    // report transform: normalize (per row) then sort (O(n log n), with a Date parse per date
    // comparison). Accumulates across sections into the page's run.
    markTransformStart()
    const normalized = normalizeReportTableRows(toValue(options.rows), toValue(options.reportId))
    const sort = toValue(options.sort)
    // Kept deliberately even though the server already returns the page ordered (ORP-19380): media
    // normalization above can rewrite the sorted field, and it keeps null placement identical to the
    // locally-derived paths. It re-sorts one page, so the cost is bounded.
    const shaped = sort
      ? sortRows(normalized, sort.field, sort.order, { getColumn: options.getColumn })
      : normalized
    markTransformEnd()
    return shaped
  })
  const expandableFields = computed(() =>
    toValue(options.enabled) === false
      ? []
      : getExpandableComparisonFields(toValue(options.columns), baseRows.value),
  )
  const activeExpandedFields = computed(() => {
    const expandable = new Set(expandableFields.value)
    return toValue(options.expandedFields).filter((field) => expandable.has(field))
  })
  const rows = computed(() => buildComparisonRows(baseRows.value, activeExpandedFields.value))
  const renderColumns = computed(() =>
    buildExpandedComparisonColumns(
      toValue(options.columns),
      baseRows.value,
      activeExpandedFields.value,
      {
        previous: () => t('reports.table.previousColumn'),
        change: () => t('reports.table.changeColumn'),
      },
    ),
  )
  return { rows, renderColumns, expandableFields, activeExpandedFields }
}
